import { takeEvery, takeLatest } from 'redux-saga'
import { call, put, take, select, race } from 'redux-saga/effects'

import actionTypes from 'redux/actions/actionTypes'

import mpActions from 'redux/actions/matching_pairs_ct_actions'
import { matchingPairsCtSync, iconSync } from 'sync'
import callAny from './helpers/call_any'
import iconTypes from 'helpers/enums/iconTypes'
import fileUploader from 'helpers/fileUploader'
import { localize } from 'helpers/translator'

import pairValidationSelector from 'redux/selectors/matching_pairs/creation_tool_pair_validation_selector'
import saveValidationSelector from 'redux/selectors/matching_pairs/creation_tool_save_validation_selector'
import activePairSelector from 'redux/selectors/matching_pairs/creation_tool_active_pair_selector'
import entityUidSelector from 'redux/selectors/matching_pairs/creation_tool_entity_uid_selector'
import saveSelector from 'redux/selectors/matching_pairs/creation_tool_save_selector'
import cancelSelector from 'redux/selectors/matching_pairs/creation_tool_cancel_selector'


function *mpContentToolInitialize(action) {
	try {
		const response = yield callAny(matchingPairsCtSync.getMatchingPairs, action.mpUID, action.jwt)
		yield put({
			type: actionTypes.MATCHING_PAIRS_CT_INITIALIZE_SUCCESS,
			data: response.payload.matching_pairs,
		})
	} catch (error) {
		yield put({
			type: actionTypes.MATCHING_PAIRS_CT_INITIALIZE_ERROR,
		})
	}
}

function *mpContentToolSave() {
	try {
		const { mpUID, instructionsValue, timerValue } = yield select(saveSelector)
		yield callAny(matchingPairsCtSync.save, instructionsValue, timerValue, mpUID, false)
		yield put({
			type: actionTypes.MATCHING_PAIRS_CT_SAVE_SUCCESS,
		})
	} catch (error) {
		yield put({
			type: actionTypes.MATCHING_PAIRS_CT_SAVE_ERROR,
		})
	}
}

function *mpContentToolImagePicked(action) {
	try {
		const savingPair = yield select(activePairSelector)
		const presentationId = yield select(state => state.matchingPairsCt.presentationId)
		const mpUID = yield select(entityUidSelector)
		const cardUID = savingPair[action.cardName].id

		const validateResponse = yield callAny(iconSync.validateFilesPicked, action.allowedTypes, action.files['files'], presentationId, mpUID, action.force)
		const payload = validateResponse.payload.icons[0]
		const file = action.files['files'][0]
		yield put(mpActions.uploaderValidateSuccess(savingPair.clientId, payload.uploadId, action.cardName))
		if (payload.error_code === 0) {
			const {uploadResponse} = yield race({
				iconDeleted: call(takeIconDeletion, savingPair.clientId, action.cardName),
				uploadResponse: callAny(fileUploader, payload.uploadId, file, 'MatchingPairsCard', cardUID, iconTypes.ENTITY_ICON_TYPE_DEFAULT, action.allowedTypes, presentationId, mpUID, action.force),

			})
			if (uploadResponse) {
				yield put({
					type: actionTypes.MATCHING_PAIRS_CT_PAIR_IMAGE_PICK_SUCCESS,
					icon: uploadResponse.payload.icon,
					cardName: action.cardName,
					clientId: savingPair.clientId,
					uploadId: payload.uploadId,
				})
			}
		} else {
			yield put({
				type: actionTypes.MATCHING_PAIRS_CT_PAIR_IMAGE_PICK_VALIDATE_ERROR,
				message: payload.message,
				cardName: action.cardName,
				clientId: savingPair.clientId,
				error_code: payload.error_code,
			})
		}
	} catch (error) {
		const savingPair = yield select(activePairSelector)
		let message = localize('quiz_service_error_message')
		if (error.serverError.message) {
			message = error.serverError.message
		}
		yield put({
			type: actionTypes.MATCHING_PAIRS_CT_PAIR_IMAGE_PICK_UPLOAD_ERROR,
			error: error.serverError,
			message,
			cardName: action.cardName,
			clientId: savingPair.clientId,
		})
	}
}

function *mpContentToolPairSave(action) {
	try {
		const validationSelector = yield select(pairValidationSelector)
		if (validationSelector.card1.valid && validationSelector.card2.valid) {
			yield put({
				type: actionTypes.MATCHING_PAIRS_CT_PAIR_SAVE_VALIDATE_SUCCESS,
				clientId: action.clientId,
			})
		} else {
			const savingPair = yield select(activePairSelector)
			yield put({
				type: actionTypes.MATCHING_PAIRS_CT_PAIR_SAVE_VALIDATE_ERROR,
				data: validationSelector,
				clientId: savingPair.clientId,
			})
		}
	} catch (error) {
		yield put({
			type: actionTypes.MATCHING_PAIRS_CT_PAIR_SAVE_VALIDATE_ERROR,
			data: {},
		})
	}
}

function *mpContentToolPairSaveValidateSuccess() {
	try {
		const savingPair = yield select(activePairSelector)
		// stop execution here until all cards had ids
		const cardsIdsSelector = () => {
			if (savingPair.card1.id && savingPair.card2.id) {
				return true
			} else {
				return false
			}
		}
		let hasCardsIds = yield select(cardsIdsSelector)
		while (!hasCardsIds) {
			const requestActions = yield take([
				actionTypes.MATCHING_PAIRS_CT_ADD_NEW_CARD_SUCCESS,
				actionTypes.MATCHING_PAIRS_CT_ADD_NEW_CARD_ERROR,
			])
			if (requestActions.type === actionTypes.MATCHING_PAIRS_CT_ADD_NEW_CARD_ERROR) {
				throw true
			}
			hasCardsIds = yield select(cardsIdsSelector)
		}

		const mpUID = yield select(entityUidSelector)
		const response = yield callAny(matchingPairsCtSync.savePair, mpUID, savingPair)

		yield put({
			type: actionTypes.MATCHING_PAIRS_CT_PAIR_SAVE_SUCCESS,
			message: localize('matching_pairs_ct_pair_added'),
			clientId: savingPair.clientId,
			payload: response.payload,
		})
	} catch (error) {
		yield put ({
			type: actionTypes.MATCHING_PAIRS_CT_PAIR_SAVE_ERROR,
		})
	}
}

function *mpContentToolPairAddAnother() {
	try {
		const savingPair = yield select(activePairSelector)
		const validationSelector = yield select(pairValidationSelector)
		if (validationSelector.card1.valid && validationSelector.card2.valid) {
			yield put({
				type: actionTypes.MATCHING_PAIRS_CT_PAIR_ADD_ANOTHER_VALIDATE_SUCCESS,
				clientId: savingPair.clientId,
			})

			const mpUID = yield select(entityUidSelector)
			const response = yield callAny(matchingPairsCtSync.savePair, mpUID, savingPair)
			yield put({
				type: actionTypes.MATCHING_PAIRS_CT_PAIR_ADD_ANOTHER_SAVE_SUCCESS,
				message: localize('matching_pairs_ct_pair_added'),
				clientId: savingPair.clientId,
				payload: response.payload,
			})
		} else {
			yield put({
				type: actionTypes.MATCHING_PAIRS_CT_PAIR_SAVE_VALIDATE_ERROR,
				data: validationSelector,
				clientId: savingPair.clientId,
			})
		}
	} catch (error) {
		yield put({
			type: actionTypes.MATCHING_PAIRS_CT_PAIR_ADD_ANOTHER_SAVE_ERROR,
		})
	}
}

function *mpContentToolAddNewCard(action) {
	try {
		const mpUID = yield select(entityUidSelector)
		const response = yield callAny(matchingPairsCtSync.addCard, mpUID)
		yield put({
			type: actionTypes.MATCHING_PAIRS_CT_ADD_NEW_CARD_SUCCESS,
			id: response.payload.id,
			clientId: action.clientId,
			cardName: action.cardName,
		})
	} catch (error) {
		yield put({
			type: actionTypes.MATCHING_PAIRS_CT_ADD_NEW_CARD_ERROR,
		})
	}
}

function *mpContentToolSaveActivity() {
	try {
		const canSave = yield select(saveValidationSelector)

		if (canSave) {
			yield put({ type: actionTypes.MATCHING_PAIRS_CT_SAVE_ACTIVITY_VALIDATE_SUCCESS })
		} else {
			yield put({
				type: actionTypes.MATCHING_PAIRS_CT_SAVE_ACTIVITY_VALIDATE_ERROR,
				data: {
					message: localize('matching_pairs_ct_complete'),
				},
			})
		}
	} catch (error) {
		// console.log(error)
	}
}

function *mpContentToolSaveActivityValidateSuccess() {
	try {
		const { mpUID, instructionsValue, timerValue } = yield select(saveSelector)
		const response = yield callAny(matchingPairsCtSync.save, instructionsValue, timerValue, mpUID, true)

		yield put({
			type: actionTypes.MATCHING_PAIRS_CT_SAVE_ACTIVITY_SUCCESS,
			redirectUrl: response.payload.redirectUrl,
			metrics: response.payload.metrics,
		})
	} catch (error) {
		yield put({
			type: actionTypes.MATCHING_PAIRS_CT_SAVE_ACTIVITY_ERROR,
			data: error,
		})
	}
}

function *mpContentToolDeletePairConfirmed(action) {
	try {
		const mpUID = yield select(entityUidSelector)
		yield callAny(matchingPairsCtSync.deletePair, action.id, mpUID)
		yield put({
			type: actionTypes.MATCHING_PAIRS_CT_DELETE_PAIR_SUCCESS,
			id: action.id,
			message: localize('matching_pairs_ct_delete_removed'),
		})
	} catch (error) {
		yield put({
			type: actionTypes.MATCHING_PAIRS_CT_DELETE_PAIR_ERROR,
		})
	}
}

function *mpContentToolCancelChanges() {
	try {
		const isModified = yield select(cancelSelector)
		if (isModified) {
			yield put({
				type: actionTypes.MATCHING_PAIRS_CT_CANCEL_MP_CONFIRM,
			})
		} else {
			yield put({
				type: actionTypes.MATCHING_PAIRS_CT_CANCEL_MP_CONFIRMED,
			})
		}
	} catch (error) {
		// console.log(error)
	}
}

function *takeIconDeletion(clientId, cardName) {
	let isDeleted = false
	while (!isDeleted) {
		const action = yield take(actionTypes.MATCHING_PAIRS_CT_PAIR_DISCARD_CONTENT)
		if (clientId === action.clientId && cardName === action.cardName) {
			isDeleted = true
		}
	}
}

export default [
	takeLatest([
		actionTypes.MATCHING_PAIRS_CT_INITIALIZE_ROUTE,
		actionTypes.MATCHING_PAIRS_SCREENSHOT_INITIALIZE_ROUTE,
		actionTypes.MATCHING_PAIRS_INITIALIZE_STANDALONE,
		actionTypes.MATCHING_PAIRS_UPDATE_CREATION_TOOL,
	], mpContentToolInitialize),
	takeLatest([
		actionTypes.MATCHING_PAIRS_CT_INSTRUCTIONS_BLUR,
		actionTypes.MATCHING_PAIRS_CT_SAVE_TIMER,
		actionTypes.MATCHING_PAIRS_CT_DELETE_TIMER,
	], mpContentToolSave),
	takeLatest(actionTypes.MATCHING_PAIRS_CT_PAIR_IMAGE_PICKED, mpContentToolImagePicked),
	takeLatest(actionTypes.MATCHING_PAIRS_CT_PAIR_SAVE, mpContentToolPairSave),
	takeLatest(actionTypes.MATCHING_PAIRS_CT_PAIR_SAVE_VALIDATE_SUCCESS, mpContentToolPairSaveValidateSuccess),
	takeLatest(actionTypes.MATCHING_PAIRS_CT_PAIR_ADD_ANOTHER, mpContentToolPairAddAnother),
	takeEvery([
		actionTypes.MATCHING_PAIRS_CT_PAIR_ADD_AN_IMAGE,
		actionTypes.MATCHING_PAIRS_CT_CARD_NEW_TEXT,
	], mpContentToolAddNewCard),
	takeLatest(actionTypes.MATCHING_PAIRS_CT_SAVE_ACTIVITY, mpContentToolSaveActivity),
	takeLatest(actionTypes.MATCHING_PAIRS_CT_SAVE_ACTIVITY_VALIDATE_SUCCESS, mpContentToolSaveActivityValidateSuccess),
	takeLatest(actionTypes.MATCHING_PAIRS_CT_PAIR_DELETE_CONFIRMED, mpContentToolDeletePairConfirmed),
	takeLatest(actionTypes.MATCHING_PAIRS_CT_CANCEL_MP, mpContentToolCancelChanges),
]
